import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import LanguagePickerButton from '@/components/LanguagePickerButton';
import AppHeader from '@/components/AppHeader';
import Recaptcha, { RECAPTCHA_ENABLED } from '@/components/Recaptcha';
import { extractErrorMessage } from '@/lib/errors';
import { shopInviteAPI } from '@/lib/api';
import { Loader2, Leaf, Store, ShoppingCart, Check, Mail, Truck } from 'lucide-react';

// Country codes (same as login)
const COUNTRY_CODES = [
  { code: '+234', country: 'Nigeria' },
  { code: '+254', country: 'Kenya' },
  { code: '+233', country: 'Ghana' },
  { code: '+27', country: 'South Africa' },
  { code: '+256', country: 'Uganda' },
  { code: '+255', country: 'Tanzania' },
  { code: '+251', country: 'Ethiopia' },
  { code: '+20', country: 'Egypt' },
  { code: '+1', country: 'Canada/USA' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'India' },
  { code: '+86', country: 'China' },
].sort((a, b) => a.country.localeCompare(b.country));

// Manufacturers aren't offered at signup (beta feedback): their
// accounts are set up by the African Farm team.
const USER_TYPES = [
  { id: 'farmer', icon: Leaf },
  { id: 'distributor', icon: Store },
  { id: 'shopkeeper', icon: ShoppingCart },
  // Farm produce marketplace (PR 67).
  { id: 'offtaker', icon: Truck },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Parse incoming phone from login redirect
  const incomingPhone = location.state?.phone || '';
  const [countryCode, setCountryCode] = useState(() => {
    if (incomingPhone) {
      const match = COUNTRY_CODES.find(c => incomingPhone.startsWith(c.code));
      return match?.code || '+234';
    }
    return '+234';
  });
  const [phone, setPhone] = useState(() => {
    if (incomingPhone) {
      const match = COUNTRY_CODES.find(c => incomingPhone.startsWith(c.code));
      return match ? incomingPhone.slice(match.code.length) : incomingPhone;
    }
    return '';
  });
  const [fullName, setFullName] = useState('');
  // National Identity Number: required, 11 digits (stored encrypted).
  const [nin, setNin] = useState('');
  const [userType, setUserType] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // A shop invited by a distributor arrives with ?invite=<token> (PR 66):
  // fill in what the invite knows, and send the token so the new account
  // is linked to the distributor and gets their products.
  const inviteToken = new URLSearchParams(location.search).get('invite');
  const [invite, setInvite] = useState(null);
  const [inviteExpired, setInviteExpired] = useState(false);
  const [email, setEmail] = useState('');
  React.useEffect(() => {
    if (!inviteToken) return;
    shopInviteAPI.lookup(inviteToken).then((res) => {
      const found = res.data;
      setInvite(found);
      setFullName((v) => v || found.name || '');
      setUserType('shopkeeper');
      setEmail(found.email || '');
      if (found.phone) {
        const match = COUNTRY_CODES.find((c) => found.phone.startsWith(c.code));
        setCountryCode(match?.code || '+234');
        setPhone(match ? found.phone.slice(match.code.length) : found.phone);
      }
    }).catch(() => setInviteExpired(true));
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast({ title: t('auth.register.errors.noName'), variant: 'destructive' });
      return;
    }
    if (!/^\d{11}$/.test(nin)) {
      toast({ title: t('auth.register.errors.invalidNin'), variant: 'destructive' });
      return;
    }
    if (!phone || phone.length < 7) {
      toast({ title: t('auth.register.errors.invalidPhone'), variant: 'destructive' });
      return;
    }
    if (!userType) {
      toast({ title: t('auth.register.errors.noType'), variant: 'destructive' });
      return;
    }
    if (password.length < 8) {
      toast({ title: t('auth.register.errors.shortPassword'), variant: 'destructive' });
      return;
    }
    if (RECAPTCHA_ENABLED && !captchaToken) {
      toast({ title: t('auth.register.errors.captcha'), variant: 'destructive' });
      return;
    }

    const fullPhone = `${countryCode}${phone.replace(/^0+/, '')}`;

    setLoading(true);
    try {
      await register({
        full_name: fullName,
        nin,
        phone: fullPhone,
        user_type: userType,
        password,
        recaptcha_token: captchaToken,
        ...(inviteToken ? { invite_token: inviteToken } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      toast({ title: t('auth.register.success') });
      navigate('/verify-otp');
    } catch (error) {
      // v2 tokens are single-use; reset before the user retries, or
      // the next submit always fails with "Captcha check failed".
      recaptchaRef.current?.reset();
      const message = extractErrorMessage(error, t('auth.register.errors.failed'));
      if (error?.response?.status === 503) {
        // No code can be sent right now (texting paused or not set up).
        toast({ title: t('auth.register.errors.smsUnavailable'), variant: 'destructive' });
      } else if (message.includes('NIN is already used')) {
        toast({ title: t('auth.register.errors.ninTaken'), variant: 'destructive' });
      } else if (message.toLowerCase().includes('already exists')) {
        toast({ title: t('auth.register.errors.exists'), variant: 'destructive' });
        navigate('/login');
      } else {
        toast({ title: message, variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Shared branded header — logo centered, language chip on the right */}
      <AppHeader trailing={<LanguagePickerButton />} />

      <div className="flex-1 p-6 overflow-auto">
        {/* Header */}
        <div className="text-center mt-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.register.title')}</h1>
          <p className="text-gray-600">{t('auth.register.subtitle')}</p>
        </div>

      {invite && (
        <div className="mb-4 rounded-xl border border-farm-primary/30 bg-farm-primary/10 p-4 text-sm" data-testid="register-invite-banner">
          <p className="font-semibold text-gray-900">{t('auth.register.invite.title', { name: invite.distributor_name })}</p>
          <p className="text-gray-700">
            {invite.phone && invite.email
              ? t('auth.register.invite.useBoth', { phone: invite.phone, email: invite.email })
              : invite.phone
                ? t('auth.register.invite.usePhone', { phone: invite.phone })
                : t('auth.register.invite.useEmail', { email: invite.email })}
          </p>
        </div>
      )}
      {inviteExpired && (
        <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {t('auth.register.invite.expired')}
        </p>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 space-y-4">
        {/* Full Name */}
        <div>
          <label htmlFor="register-name" className="block text-sm font-semibold text-gray-900 mb-2">{t('auth.register.fullNameLabel')}</label>
          <Input
            id="register-name"
            placeholder={t('auth.register.fullNamePlaceholder')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="h-14 bg-white border-gray-200 text-lg"
            data-testid="register-name-input"
          />
        </div>

        {/* National Identity Number, under the name */}
        <div>
          <label htmlFor="register-nin" className="block text-sm font-semibold text-gray-900 mb-2">{t('auth.register.ninLabel')}</label>
          <Input
            id="register-nin"
            inputMode="numeric"
            autoComplete="off"
            maxLength={11}
            placeholder={t('auth.register.ninPlaceholder')}
            value={nin}
            onChange={(e) => setNin(e.target.value.replace(/\D/g, '').slice(0, 11))}
            className="h-14 bg-white border-gray-200 text-lg tracking-wider"
            aria-describedby="register-nin-hint"
            required
            data-testid="register-nin-input"
          />
          <p id="register-nin-hint" className="text-xs text-gray-500 mt-1">{t('auth.register.ninHint')}</p>
        </div>

        {/* Phone Number */}
        <div>
          <label htmlFor="register-phone" className="block text-sm font-semibold text-gray-900 mb-2">{t('auth.register.phoneLabel')}</label>
          <div className="flex gap-2">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger className="w-[130px] h-14 bg-white border-gray-200" data-testid="register-country-code">
                <SelectValue>
                  <span>{countryCode}</span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {COUNTRY_CODES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.code} {country.country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="register-phone"
              type="tel"
              placeholder={t('auth.register.phonePlaceholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
              className="flex-1 h-14 bg-white border-gray-200 text-lg"
              data-testid="register-phone-input"
            />
          </div>
        </div>

        {/* Email: only for invited shops, to match an email invitation */}
        {(invite || inviteExpired) && (
          <div>
            <label htmlFor="register-email" className="block text-sm font-semibold text-gray-900 mb-2">
              <Mail className="inline w-4 h-4 mr-1" />{t('auth.register.invite.emailLabel')}
            </label>
            <Input id="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="h-14 bg-white border-gray-200 text-lg" data-testid="register-email-input" />
          </div>
        )}

        {/* Password */}
        <div>
          <label htmlFor="register-password" className="block text-sm font-semibold text-gray-900 mb-2">{t('auth.register.passwordLabel')}</label>
          <PasswordInput
            id="register-password"
            placeholder={t('auth.register.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-14 bg-white border-gray-200 text-lg"
            testId="register-password-input"
            autoComplete="new-password"
          />
        </div>

        {/* User Type */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">{t('auth.register.userTypeLabel')}</label>
          <div className="grid grid-cols-2 gap-3">
            {USER_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => setUserType(type.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  userType === type.id
                    ? 'border-farm-primary bg-farm-primary/5'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                data-testid={`register-type-${type.id}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    userType === type.id ? 'bg-farm-primary' : 'bg-farm-primary/10'
                  }`}>
                    <type.icon className={`w-6 h-6 ${userType === type.id ? 'text-white' : 'text-farm-primary'}`} />
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    userType === type.id ? 'border-farm-primary bg-farm-primary' : 'border-gray-300'
                  }`}>
                    {userType === type.id && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <p className="font-semibold text-gray-900 text-sm mb-1">{t(`auth.register.types.${type.id}.label`)}</p>
                <p className="text-xs text-gray-500 leading-tight">{t(`auth.register.types.${type.id}.description`)}</p>
              </button>
            ))}
          </div>
        </div>

        <Recaptcha ref={recaptchaRef} onChange={setCaptchaToken} />

        {/* Submit Buttons */}
        <div className="pt-4 space-y-3">
          <Button
            type="submit"
            disabled={loading || (RECAPTCHA_ENABLED && !captchaToken)}
            className="w-full h-14 bg-farm-primary hover:bg-farm-primary-dark text-white font-semibold text-lg rounded-xl"
            data-testid="register-submit-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.register.submit')}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/login')}
            className="w-full h-14 font-semibold text-lg rounded-xl border-2"
          >
            {t('auth.register.haveAccount')}
          </Button>
        </div>
      </form>
      </div>
    </div>
  );
}
